import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { animationConfigs } from '../animations.config';

import { Animation, AnimationType } from './animations.model';
import { take } from 'rxjs';

// import { AnimationType, Animation } from 'src/app/extensions/dot-cms-uve/models/dot-cms-uve-contentlet.model';

gsap.registerPlugin(ScrollTrigger);

@Component({
  selector: 'dot-cms-animation',
  templateUrl: './dot-cms-animation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class DotCmsAnimationComponent implements AfterViewInit, OnChanges {
  @Input() animation!: Animation | null;

  private activeMouseTween?: gsap.core.Tween & { cleanup?(): void };
  private element?: HTMLElement;
  private initialized = false;
  private refreshTimeout: any;

  constructor(private cdRef: ChangeDetectorRef, private ngZone: NgZone, private elementRef: ElementRef) {}
  ngAfterViewInit() {
    if (!SSR) {
      this.element = this.elementRef.nativeElement.firstElementChild as HTMLElement;
      this.ngZone.onStable.pipe(take(1)).subscribe(() => {
        this.refreshAnimation();
        this.initialized = true;
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!SSR && changes.animation && this.initialized) {
      const previousValue = changes.animation.previousValue;
      const currentValue = changes.animation.currentValue;
      if (JSON.stringify(previousValue) !== JSON.stringify(currentValue)) {
        this.refreshAnimation();
      }
    }
  }

  private refreshAnimation() {
    if (!this.element || !this.animation) {
      return;
    }

    this.cleanupAnimation();

    this.ngZone.runOutsideAngular(() => {
      this.applyAnimation(this.animation!, this.element!);
    });
    this.ngZone.run(() => this.cdRef.markForCheck());
    this.ngZone.runOutsideAngular(() => {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = setTimeout(() => {
        ScrollTrigger.refresh(true);
      }, 200);
    });
  }

  private applyAnimation(cfg: Animation, el: HTMLElement) {
    const { category, type, dynamicSettings } = cfg;

    if (category === 'mouse') {
      const fn = animationConfigs.mouse?.[type as AnimationType];
      if (!fn) {
        return;
      }
      this.activeMouseTween = fn(dynamicSettings, el, {});
      return;
    }

    const fn = animationConfigs[category]?.[type as AnimationType];
    if (!fn) {
      return;
    }

    const opts: any = {
      duration: dynamicSettings.duration ?? 0.8,
      ease: dynamicSettings.easing ?? 'power1.inOut',
      repeat: category === 'loop' ? -1 : 0,
      direction: dynamicSettings.direction,
      delay: dynamicSettings.delay ?? 0,
      repeatDelay: dynamicSettings.repeat_delay ?? 0,
      angle: dynamicSettings.angle,
    };

    if (category === 'scroll') {
      fn(dynamicSettings, el, {
        scrollTrigger: {
          trigger: el,
          start: dynamicSettings.start || 'top 80%',
          end: dynamicSettings.end || 'bottom 20%',
          toggleActions: dynamicSettings.toggleActions || 'play none none reverse',
          scrub: dynamicSettings.scrub ?? false,
          markers: dynamicSettings.markers ?? false,
        },
        ...opts,
      });
    } else {
      fn(dynamicSettings, el, opts);
    }
  }

  ngOnDestroy() {
    this.cleanupAnimation();
    if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
  }

  cleanupAnimation() {
    this.activeMouseTween?.cleanup?.();
    this.activeMouseTween?.kill();
    ScrollTrigger.getAll().forEach(trigger => {
      if (trigger.trigger === this.element) trigger.kill();
    });
    gsap.killTweensOf(this.element);
  }
}
